import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { CurrencyPipe } from '../../pipes/currency.pipe';
import { type IconName } from '../../../icons/icon.component';
import { IconComponentV2 } from '../../icon-component/icon.component';
import { GlobalDateFormat, GlobalDateFormatPipe } from '../../../pipes/global-date-format.pipe';
import { TooltipComponent } from '../../ui/tooltip/tooltip.component';

export interface TableHeader {
  label: string;
  key: string;
  transform?: string;
  dateFormat?: GlobalDateFormat;
  align?: 'text-left' | 'text-right' | 'text-center';
  isPrevious?: boolean;
  showProgress?: boolean;
  showTooltip?: boolean;
  showIcon?: boolean;
  noTransform?: boolean; // If true, skip automatic transformations (date, currency, etc.)
  renderAsHtml?: boolean; // If true, render cell value with innerHTML (e.g. SafeHtml)
  icon?: IconName;
  showHeaderIcon?: boolean;
  textClass?: string; // Custom class for text styling (e.g. 'text-green-600')
  colClass?: string;
  actionTooltip?: string;
  showActionTooltip?: boolean;
}

@Component({
  standalone: true,
  selector: 'app-scroll-table',
  templateUrl: './scroll-table.component.html',
  styleUrl: './scroll-table.component.scss',
  providers: [CurrencyPipe, GlobalDateFormatPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, IconComponentV2, TooltipComponent]
})
export class ScrollTableComponent implements OnChanges {
  @ViewChild('tableTooltip', { static: false })
  tooltipRef!: ElementRef<HTMLDivElement>;
  @Input() isCheckboxShow: boolean = false;
  @Input() tableData: any[] | null = null;
  @Input() previousData: any[] | null = null;
  @Input() tooltipData: { key: string; label: string }[] = [];
  @Input() headers: TableHeader[] | null = null;
  @Input() showHeading: boolean = true;
  @Input() tableKey: string = "";
  @Input() isColFlex: boolean = false;
  @Input() showHeaderIcon: boolean = true;
  @Input() showTableWithBorderAndRadius: boolean = true;
  @Input() maxHeight: string = 'none';
  @Input() tableClass: string = '';

  @Output() fetchData = new EventEmitter<any>();
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() cellHover = new EventEmitter<{ row: any; event: MouseEvent } | null>();
  tooltipHTML = '';
  finalValue: { type: string; amount: number; percent: string | number } = {
    percent: '0',
    type: '',
    amount: 0,
  };

  sortState: { [tableKey: string]: { key: string, order: 'asc' | 'desc' } } = {};

  constructor(private currencyPipe: CurrencyPipe, private datePipe: GlobalDateFormatPipe) { }

  // Computed property for dynamic header positioning
  get headerTopPosition(): string {
    return this.showHeading ? 'top-[70px]' : 'top-0';
  }

  ngOnChanges(_: SimpleChanges) {
    const progressHeader = this.headers?.find((h) => h.showProgress);
    if (progressHeader) {
      this.getTotalGrowth(progressHeader.key);
    }
  }

  /** Get value from row with transformations */
  getValue(key: string, data: any, index: number, transform?: string, noTransform?: boolean, dateFormat?: GlobalDateFormat): any {
    if (!key) return '';
    const row = Array.isArray(data) ? data[index] : data;
    if (!row || row[key] == null || row[key] === 'undefined') return 0;

    // Skip all transformations if noTransform flag is set
    if (noTransform) {
      return row[key];
    }

    if (key.toLowerCase().includes('amount')) {
      return this.currencyPipe.transform(row[key]);
    }

    if (
      key.toLowerCase().includes('start') ||
      key.toLowerCase().includes('date')
    ) {
      return this.datePipe.transform(row[key], dateFormat || (transform as GlobalDateFormat) || 'shortDate');
    }

    if (transform === 'number') {
      const val = Number(row[key]);
      return isNaN(val) ? row[key] : val.toLocaleString('en-US');
    }

    return row[key];
  }

  /** Get correct row (current vs previous) */
  resolveRow(h: TableHeader, row: any, index: number): any {
    return h.isPrevious && this.previousData?.length
      ? this.previousData[index]
      : row;
  }

  toggleView(row: any) {
    this.fetchData.emit(row);
  }

  /** Growth calculation */
  private getTotalGrowth(key: string) {
    const sum = (arr?: any[]) =>
      arr?.reduce(
        (curr, acc) => curr + (acc[key] && acc[key] !== 'N.A.' ? +acc[key] : 0),
        0
      ) ?? 0;

    this.finalValue = this.calculateProfitLoss(
      sum((this.tableData || []) as any[]),
      sum((this.previousData || []) as any[])
    );
  }

  private calculateProfitLoss(a: number, b: number) {
    if (b < a) {
      const profit = a - b;
      let value = {
        type: 'Profit',
        amount: profit,
        percent: '+' + (b == 0 ? profit : ((profit / b) * 100).toFixed(2)),
      };

      if (String(value.percent) == '-Infinity')
        value.percent = '+' + String(profit);

      return value;
    } else if (b > a) {
      const loss = b - a;
      let value = {
        type: 'Loss',
        amount: loss,
        percent: '-' + (b == 0 ? loss : ((loss / b) * 100).toFixed(2)),
      };

      if (value.percent == '-Infinity') value.percent = '-' + String(loss);

      return value;
    }
    return { type: 'No Profit No Loss', amount: 0, percent: 0 };
  }

  /** Tooltip handling */
  triggerTooltip(event: MouseEvent, header: TableHeader, row: any) {
    if (
      !header.showTooltip ||
      !row ||
      !(this.tooltipData && this.tooltipData.length)
    )
      return;

    this.tooltipHTML = this.tooltipData
      .map(
        (el) => `
        <div class="flex items-center justify-between gap-2">
          <span class="mr-3 text-[#B6BCC4] text-base">${el.label}:</span>
          <span class="text-white">${row[el.key] ?? '-'}</span>
        </div>`
      )
      .join('');

    this.tooltipRef.nativeElement.classList.remove('hidden');
    this.showTooltip(event);
  }

  showTooltip(event: MouseEvent) {
    const tooltipEl = this.tooltipRef.nativeElement;
    const parentRect = tooltipEl.parentElement?.getBoundingClientRect();
    if (!parentRect) return;

    const { offsetWidth: w, offsetHeight: h } = tooltipEl;
    const offset = 20;
    const margin = 15;

    let left = event.layerX + offset;
    let top = event.layerY + offset;

    if (event.layerX + w + offset > parentRect.width)
      left = event.layerX - w - margin;
    if (event.layerY + h > parentRect.height) top = event.layerY - h + offset;

    left = Math.max(margin, Math.min(left, parentRect.width - w - margin));
    top = Math.max(margin, Math.min(top, parentRect.height - h));

    Object.assign(tooltipEl.style, {
      position: 'absolute',
      left: `${left}px`,
      top: `${top}px`,
    });
  }

  hideTooltip() {
    this.tooltipRef.nativeElement.classList.add('hidden');
  }

  getProgressForRow(value1: any, value2: any, key: any) {
    if (!value1 || !value2 || !key) return [];
    return [this.calculateProfitLoss(value1[key], value2[key])];
  }



  /* checkbox selection */

  selectedRows = new Set<any>()


  isRowSelected(row: any): boolean {
    return this.selectedRows.has(row);
  }

  toggleRowSelection(row: any, event: Event): void {
    event.stopPropagation();
    if (this.selectedRows.has(row)) {
      this.selectedRows.delete(row);
    } else {
      this.selectedRows.add(row);
    }
    this.selectionChange.emit(this.getSelectedRows());
  }

  isAllSelected(): boolean {
    return (this.tableData?.length ?? 0) > 0 &&
      this.selectedRows.size === (this.tableData?.length ?? 0);
  }

  isSomeSelected(): boolean {
    return this.selectedRows.size > 0 &&
      this.selectedRows.size < (this.tableData?.length ?? 0);
  }

  toggleAllRows(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.tableData?.forEach(row => this.selectedRows.add(row));
    } else {
      this.selectedRows.clear();
    }
    this.selectionChange.emit(this.getSelectedRows());
  }

  getSelectedRows(): any[] {
    return Array.from(this.selectedRows);
  }

  clearSelection(): void {
    this.selectedRows.clear();
    this.selectionChange.emit([]);
  }

  getSelectedCount(): number {
    return this.selectedRows.size;
  }

  sortData(array: any[], key: string, tableKey: string) {
    if (!array?.length) return;

    // Normalize key comparison (case-insensitive)
    let normalizedKey = Object.keys(array[0]).find(
      k => k.toLowerCase() === key.toLowerCase()
    );

    if (!normalizedKey) return; // No matching key found

    let current = this.sortState[tableKey];
    const newOrder =
      current && current.key === normalizedKey && current.order === 'asc'
        ? 'desc'
        : 'asc';
    this.sortState[tableKey] = { key: normalizedKey, order: newOrder };

    array.sort((a, b) => {
      let valA = a[normalizedKey] ?? '';
      let valB = b[normalizedKey] ?? '';

      if (!isNaN(valA) && !isNaN(valB)) {
        return newOrder === 'asc' ? valA - valB : valB - valA;
      }

      return newOrder === 'asc'
        ? valA.toString().localeCompare(valB.toString())
        : valB.toString().localeCompare(valA.toString());
    });
  }

  // New cell hover methods for custom tooltips
  onCellMouseOver(event: MouseEvent, header: TableHeader, row: any) {
    // Only emit if this header should show tooltip
    if (header.showTooltip && row) {
      this.cellHover.emit({ row, event });
    }
  }

  onCellMouseLeave() {
    this.cellHover.emit(null);
  }

  onCellMouseMove(event: MouseEvent) {
    // Update position if needed
    if (this.cellHover.observed) {
      // Emit current position
    }
  }


  getTableClasses() {
    let classes = 'rounded-[14px] px-0 pb-0 transition-all duration-300 dark:bg-surface-1 bg-white h-full max-h-full flex flex-col';
    if (this.showTableWithBorderAndRadius) {
      classes += ' border border-[#EAEAEA] dark:border-line overflow-hidden';
    }
    return classes;
  }

  getTableHeadingClasses() {
    return this.showTableWithBorderAndRadius
      ? 'first:rounded-tl-[14px] last:rounded-tr-[14px] backdrop-blur-3xl bg-[#F5F5F5] dark:bg-surface-3'
      : 'backdrop-blur-3xl bg-[#F5F5F5] dark:bg-surface-3';
  }

  getTableBorderRadiusClasses() {
    return this.showTableWithBorderAndRadius ? 'rounded-tl-[14px] rounded-tr-[14px]' : '';
  }


}
